import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import SearchableDropdown from '../components/SearchableDropdown';
import CountryPhoneInput from '../components/CountryPhoneInput';
import axios from 'axios';
import { trackFeatureUsage, trackSearch, trackFormSubmission, trackEvent } from '../utils/analytics';
import { calculateIdealWeight } from '../utils/healthCalculations';

const Members = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [hierarchyLevels, setHierarchyLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortField, setSortField] = useState('fullName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(15);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [expandedLevels, setExpandedLevels] = useState(new Set([1])); // Level 1 expanded by default
  const [hierarchySearchQuery, setHierarchySearchQuery] = useState('');
  const [filteredHierarchyLevels, setFilteredHierarchyLevels] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [newMember, setNewMember] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    gender: 'MALE',
    phone: '',
    phoneCountryId: null,
    address: '',
    pcId: '',
    customerType: 'ONLINE',
    referredById: '',
    referredBySearch: '',
    showReferralDropdown: false,
    filteredReferralOptions: [],
    checkupDay: 'MONDAY',
    program: '',
    memberRoleCode: 'CUSTOMER',
    programDiscountSlabId: '',
    productDiscountSlabId: '',
    dateOfBirth: '',
    age: '',
    heightCm: '',
    idealWeightKg: '',
    initialWeightKg: ''
  });
  const [immediateChildren, setImmediateChildren] = useState([]);
  const [programOptions, setProgramOptions] = useState([]);
  const [discountPlans, setDiscountPlans] = useState([]);
  const [productDiscountPlans, setProductDiscountPlans] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    fetchHierarchicalMembers();
    fetchImmediateChildren();
    fetchDiscountPlans();
    // Track members page view
    trackFeatureUsage('Members Management', 'view');
  }, []);

  useEffect(() => {
    // Filter hierarchy levels based on search query
    if (!hierarchySearchQuery) {
      setFilteredHierarchyLevels(hierarchyLevels);
    } else {
      const filtered = hierarchyLevels.map(level => 
        level.filter(member => 
          member.fullName?.toLowerCase().includes(hierarchySearchQuery.toLowerCase()) ||
          member.email?.toLowerCase().includes(hierarchySearchQuery.toLowerCase())
        )
      );
      setFilteredHierarchyLevels(filtered);
      
      // Track search usage
      if (hierarchySearchQuery.length >= 2) {
        trackSearch(hierarchySearchQuery, 'members_hierarchy');
      }
    }
  }, [hierarchyLevels, hierarchySearchQuery]);

  useEffect(() => {
    // Update program options based on customer type
    if (newMember.customerType === 'ONLINE') {
      setProgramOptions([
        'Basic 21 Days',
        'Advanced 21 Days',
        '30 Days Weight Loss'
      ]);
    } else if (newMember.customerType === 'CLUB') {
      setProgramOptions([
        'Trial Program',
        'Fitness Plan',
        'Advanced Weight Loss',
        'Premium Weight Loss'
      ]);
    }
    // Reset program selection when customer type changes
    setNewMember(prev => ({ ...prev, program: '' }));
  }, [newMember.customerType]);

  const fetchMembers = async () => {
    try {
      setSearchLoading(true);
      
      // Check if user is super admin
      const isSuperAdmin = user?.systemRoles?.some(role => role.code === 'SYSTEM_ADMIN');
      
      const endpoint = isSuperAdmin ? '/api/members/all-paged' : '/api/members/direct-children-paged';
      const params = {
        page: currentPage,
        size: pageSize,
        search: searchQuery,
        sortBy: sortField,
        sortDir: sortDirection
      };
      
      const response = await axios.get(endpoint, { params });
      const data = response.data;
      
      setMembers(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
    } catch (error) {
      console.error('Failed to fetch members:', error);
      setError('Failed to load members. Please try again.');
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const fetchHierarchicalMembers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/members/hierarchical-system-editors');
      setHierarchyLevels(response.data);
      
      // Calculate total elements for display
      const total = response.data.reduce((sum, level) => sum + level.length, 0);
      setTotalElements(total);
    } catch (error) {
      console.error('Failed to fetch hierarchical members:', error);
      setError('Failed to load hierarchical members. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleLevel = (level) => {
    const newExpanded = new Set(expandedLevels);
    if (newExpanded.has(level)) {
      newExpanded.delete(level);
    } else {
      newExpanded.add(level);
    }
    setExpandedLevels(newExpanded);
  };

  const fetchImmediateChildren = async () => {
    try {
      const response = await axios.get('/api/customers/referral-options');
      setImmediateChildren(response.data);
    } catch (error) {
      console.error('Failed to fetch referral options:', error);
    }
  };

  const fetchDiscountPlans = async () => {
    try {
      const response = await axios.get('/api/products/discount-plans');
      setDiscountPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch discount plans:', error);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(0); // Reset to first page when sorting
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(0); // Reset to first page when searching
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      await axios.post('/api/members/add-customer', newMember);
      setShowAddForm(false);
      setShowConfirmModal(true);
      setNewMember({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        gender: 'MALE',
        phone: '',
        address: '',
        pcId: '',
        customerType: 'ONLINE',
        referredById: '',
        referredBySearch: '',
        showReferralDropdown: false,
        filteredReferralOptions: [],
        checkupDay: 'MONDAY',
        program: '',
        memberRoleCode: 'CUSTOMER',
        programDiscountSlabId: '',
        productDiscountSlabId: '',
        dateOfBirth: '',
        age: '',
        heightCm: '',
        idealWeightKg: '',
        initialWeightKg: ''
      });
      setCurrentPage(0); // Reset to first page
      fetchMembers(); // Refresh the list
    } catch (error) {
      console.error('Failed to add member:', error);
      setFormError(error.response?.data?.message || 'Failed to add member. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'var(--success)';
      case 'INACTIVE': return 'var(--warning)';
      case 'BLOCKED': return 'var(--error)';
      default: return 'var(--gray-dark)';
    }
  };

  const [viewMember, setViewMember] = useState(null);
  const [editMember, setEditMember] = useState(null);
  const [memberDetails, setMemberDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const handleViewMember = async (member) => {
    trackEvent('view_member_details', {
      event_category: 'Member Management',
      event_label: member.fullName,
      member_id: member.id
    });
    
    setDetailsLoading(true);
    try {
      const response = await axios.get(`/api/members/${member.id}`);
      setMemberDetails(response.data);
      setViewMember(member);
    } catch (error) {
      console.error('Failed to fetch member details:', error);
      setFormError('Failed to load member details. Please try again.');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleEditMember = async (member) => {
    setDetailsLoading(true);
    try {
      const response = await axios.get(`/api/members/${member.id}`);
      const details = response.data;
      
      // Convert the detailed member data to the form format
      setNewMember({
        firstName: details.firstName || '',
        lastName: details.lastName || '',
        email: details.email || '',
        password: '', // Don't populate password for security
        gender: details.gender || 'MALE',
        phone: details.phone || '',
        address: details.address || '',
        pcId: details.pcId || '',
        customerType: details.customerType || 'ONLINE',
        referredById: details.referredBy?.id || '',
        checkupDay: details.checkupDay || 'MONDAY',
        program: details.program || '',
        memberRoleCode: details.memberRole?.code || 'CUSTOMER',
        programDiscountSlabId: details.programDiscountPlan?.id || '',
        productDiscountSlabId: details.productDiscountPlan?.id || ''
      });
      
      setEditMember(member);
    } catch (error) {
      console.error('Failed to fetch member details for editing:', error);
      setFormError('Failed to load member details for editing. Please try again.');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      await axios.put(`/api/members/${editMember.id}`, newMember);
      setEditMember(null);
      setShowConfirmModal(true);
      setNewMember({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        gender: 'MALE',
        phone: '',
        address: '',
        pcId: '',
        customerType: 'ONLINE',
        referredById: '',
        referredBySearch: '',
        showReferralDropdown: false,
        filteredReferralOptions: [],
        checkupDay: 'MONDAY',
        program: '',
        memberRoleCode: 'CUSTOMER',
        programDiscountSlabId: '',
        productDiscountSlabId: '',
        dateOfBirth: '',
        age: '',
        heightCm: '',
        idealWeightKg: '',
        initialWeightKg: ''
      });
      fetchMembers(); // Refresh the list
    } catch (error) {
      console.error('Failed to update member:', error);
      setFormError(error.response?.data?.message || 'Failed to update member. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading">Loading members...</div>
      </div>
    );
  }

  return (
    <div className="page">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div style={{ flex: '1', minWidth: '200px' }}>
          <h1 className="page-title" style={{ margin: 0 }}>Manage Associates</h1>
          {user?.systemRoles?.some(role => role.code === 'SYSTEM_ADMIN') && (
            <div style={{ 
              fontSize: '14px', 
              color: 'var(--primary-blue)', 
              fontWeight: '500',
              marginTop: '5px'
            }}>
              🔑 Super Admin Mode - Viewing entire system hierarchy
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
            {user?.systemRoles?.some(role => role.code === 'SYSTEM_ADMIN') 
              ? `System Hierarchy - All Users (${totalElements} total)`
              : `Network Hierarchy -  (${totalElements} total)`
            }
          </h3>
          <div style={{ position: 'relative', width: '300px' }}>
            <input
              type="text"
              className="form-input"
              placeholder="Search associates in hierarchy..."
              value={hierarchySearchQuery}
              onChange={(e) => setHierarchySearchQuery(e.target.value)}
              style={{ margin: 0, paddingRight: '35px' }}
            />
            {hierarchySearchQuery && (
              <button
                onClick={() => setHierarchySearchQuery('')}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--gray-dark)'
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        <div style={{ fontSize: '14px', color: 'var(--gray-dark)', marginBottom: '20px' }}>
          {user?.systemRoles?.some(role => role.code === 'SYSTEM_ADMIN') 
            ? 'Organized by hierarchy levels • Entire system view'
            : 'Organized by hierarchy levels '
          }
        </div>

        {filteredHierarchyLevels.length === 0 || filteredHierarchyLevels.every(level => level.length === 0) ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
            {hierarchySearchQuery 
              ? `No members found matching "${hierarchySearchQuery}".` 
              : (user?.systemRoles?.some(role => role.code === 'SYSTEM_ADMIN') 
                  ? 'No SYSTEM_EDITOR users found in the entire system.' 
                  : 'No SYSTEM_EDITOR members found in your network hierarchy.')
            }
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {filteredHierarchyLevels.map((levelMembers, levelIndex) => {
              if (levelMembers.length === 0) return null;
              
              const level = levelIndex + 1;
              const isExpanded = expandedLevels.has(level);
              
              return (
                    <div key={level} style={{
                      border: '2px solid var(--light-blue)',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      {/* Accordion Header */}
                      <div
                        onClick={() => toggleLevel(level)}
                        style={{
                          background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--dark-blue) 100%)',
                          color: 'var(--white)',
                          padding: '15px 20px',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontWeight: '500'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '16px' }}>
                            Level {level}
                          </span>
                          <span style={{
                            background: 'rgba(255, 255, 255, 0.2)',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}>
                            {levelMembers.length} member{levelMembers.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <span style={{
                          fontSize: '18px',
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease'
                        }}>
                          ▼
                        </span>
                      </div>

                      {/* Accordion Content */}
                      {isExpanded && (
                        <div style={{ background: 'var(--white)' }}>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ 
                              width: '100%', 
                              borderCollapse: 'collapse',
                              fontSize: '14px'
                            }}>
                              <thead>
                                <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid var(--light-blue)' }}>
                                    Full Name
                                  </th>
                                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid var(--light-blue)' }}>
                                    Email
                                  </th>
                                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid var(--light-blue)' }}>
                                    Role
                                  </th>
                                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid var(--light-blue)' }}>
                                    Status
                                  </th>
                                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid var(--light-blue)' }}>
                                    Joined Date
                                  </th>
                                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid var(--light-blue)' }}>
                                    Active Customers
                                  </th>
                                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid var(--light-blue)' }}>
                                    Inactive Customers
                                  </th>
                                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid var(--light-blue)' }}>
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {levelMembers.map((member, index) => (
                                  <tr 
                                    key={member.id}
                                    style={{ 
                                      backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                                      borderBottom: '1px solid var(--gray-medium)'
                                    }}
                                  >
                                    <td style={{ padding: '12px', fontWeight: '500' }}>
                                      {member.fullName || 'N/A'}
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                      {member.email}
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                      <span style={{
                                        background: 'var(--primary-blue)',
                                        color: 'var(--white)',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '12px'
                                      }}>
                                        {member.memberRole?.displayName || 'No Role'}
                                      </span>
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                      <span style={{
                                        color: getStatusColor(member.status),
                                        fontWeight: '500'
                                      }}>
                                        {member.status}
                                      </span>
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                      {member.joinedAt ? new Date(member.joinedAt).toLocaleDateString() : 'N/A'}
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                      <span style={{
                                        display: 'inline-block',
                                        minWidth: '36px',
                                        background: '#C6F6D5',
                                        color: '#22543D',
                                        padding: '4px 8px',
                                        borderRadius: '999px',
                                        fontWeight: '600'
                                      }}>
                                        {member.activeCustomerCount ?? 0}
                                      </span>
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                      <span style={{
                                        display: 'inline-block',
                                        minWidth: '36px',
                                        background: '#FED7D7',
                                        color: '#822727',
                                        padding: '4px 8px',
                                        borderRadius: '999px',
                                        fontWeight: '600'
                                      }}>
                                        {member.inactiveCustomerCount ?? 0}
                                      </span>
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                      <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                                        <button 
                                          className="btn btn-secondary"
                                          style={{ fontSize: '11px', padding: '4px 8px' }}
                                          onClick={() => handleViewMember(member)}
                                          title="View Details"
                                          disabled={detailsLoading}
                                        >
                                          👁️
                                        </button>
                                        <button 
                                          className="btn btn-primary"
                                          style={{ fontSize: '11px', padding: '4px 8px' }}
                                          onClick={() => handleEditMember(member)}
                                          title="Edit Member"
                                          disabled={detailsLoading}
                                        >
                                          ✏️
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  );
            })}
          </div>
        )}
      </div>

      {/* Add/Edit Customer Form Modal */}
      {(showAddForm || editMember) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
              {editMember ? 'Edit Member' : 'Add New Customer'}
            </h3>

            {formError && (
              <div className="alert alert-error">{formError}</div>
            )}

            <form onSubmit={editMember ? handleUpdateMember : handleAddMember}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="firstName" className="form-label">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    className="form-input"
                    maxLength="50"
                    value={newMember.firstName}
                    onChange={(e) => setNewMember({...newMember, firstName: e.target.value})}
                    required
                    disabled={formLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName" className="form-label">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    className="form-input"
                    maxLength="50"
                    value={newMember.lastName}
                    onChange={(e) => setNewMember({...newMember, lastName: e.target.value})}
                    required
                    disabled={formLoading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="gender" className="form-label">
                  Gender *
                </label>
                <select
                  id="gender"
                  className="form-input"
                  value={newMember.gender}
                  onChange={(e) => {
                    const gender = e.target.value;
                    setNewMember({...newMember, gender: gender});
                    
                    // Auto-calculate ideal weight when gender changes
                    if (newMember.heightCm && gender) {
                      const idealWeight = calculateIdealWeight(newMember.heightCm, gender);
                      if (idealWeight) {
                        setNewMember(prev => ({...prev, idealWeightKg: idealWeight.toString()}));
                      }
                    }
                  }}
                  required
                  disabled={formLoading}
                >
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">
                    Phone Number *
                  </label>
                  <CountryPhoneInput
                    phoneValue={newMember.phone}
                    countryValue={newMember.phoneCountryId}
                    onPhoneChange={(phone) => setNewMember({...newMember, phone})}
                    onCountryChange={(countryId) => setNewMember({...newMember, phoneCountryId: countryId})}
                    disabled={formLoading}
                    required={true}
                    placeholder="Enter phone number"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    className="form-input"
                    value={newMember.email}
                    onChange={(e) => setNewMember({...newMember, email: e.target.value})}
                    required
                    disabled={formLoading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="address" className="form-label">
                  Address
                </label>
                <textarea
                  id="address"
                  className="form-input"
                  rows="3"
                  value={newMember.address}
                  onChange={(e) => setNewMember({...newMember, address: e.target.value})}
                  disabled={formLoading}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="pcId" className="form-label">
                    PC ID
                  </label>
                  <input
                    type="text"
                    id="pcId"
                    className="form-input"
                    value={newMember.pcId}
                    onChange={(e) => setNewMember({...newMember, pcId: e.target.value})}
                    disabled={formLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerType" className="form-label">
                    Customer Type *
                  </label>
                  <select
                    id="customerType"
                    className="form-input"
                    value={newMember.customerType}
                    onChange={(e) => setNewMember({...newMember, customerType: e.target.value})}
                    required
                    disabled={formLoading}
                  >
                    <option value="ONLINE">Online</option>
                    <option value="CLUB">Club</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="referredBy" className="form-label">
                    Referred By *
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      id="referredBy"
                      className="form-input"
                      placeholder="Search and select referrer..."
                      value={newMember.referredBySearch || ''}
                      onChange={(e) => {
                        const searchValue = e.target.value;
                        setNewMember({...newMember, referredBySearch: searchValue});
                        
                        // Filter options based on search
                        const allOptions = [
                          { id: user.id, fullName: `${user.fullName} (You)`, email: user.email },
                          ...immediateChildren
                        ];
                        const filtered = allOptions.filter(option => 
                          option.fullName.toLowerCase().includes(searchValue.toLowerCase()) ||
                          option.email.toLowerCase().includes(searchValue.toLowerCase())
                        );
                        setNewMember(prev => ({...prev, filteredReferralOptions: filtered}));
                      }}
                      onFocus={() => {
                        const allOptions = [
                          { id: user.id, fullName: `${user.fullName} (You)`, email: user.email },
                          ...immediateChildren
                        ];
                        setNewMember(prev => ({...prev, showReferralDropdown: true, filteredReferralOptions: allOptions}));
                      }}
                      required
                      disabled={formLoading}
                    />
                    
                    {newMember.showReferralDropdown && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'var(--white)',
                        border: '1px solid var(--gray-medium)',
                        borderRadius: '4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}>
                        {(newMember.filteredReferralOptions || []).length === 0 ? (
                          <div style={{ padding: '10px', color: 'var(--gray-dark)' }}>
                            No referral options available
                          </div>
                        ) : (
                          (newMember.filteredReferralOptions || []).map(option => (
                            <div
                              key={option.id}
                              style={{
                                padding: '10px',
                                cursor: 'pointer',
                                borderBottom: '1px solid var(--gray-light)'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--pale-blue)'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                              onClick={() => {
                                setNewMember({
                                  ...newMember,
                                  referredById: option.id,
                                  referredBySearch: option.fullName,
                                  showReferralDropdown: false,
                                  filteredReferralOptions: []
                                });
                              }}
                            >
                              <div style={{ fontWeight: '500' }}>{option.fullName}</div>
                              <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>{option.email}</div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="checkupDay" className="form-label">
                    Checkup Day *
                  </label>
                  <select
                    id="checkupDay"
                    className="form-input"
                    value={newMember.checkupDay}
                    onChange={(e) => setNewMember({...newMember, checkupDay: e.target.value})}
                    required
                    disabled={formLoading}
                  >
                    <option value="MONDAY">Monday</option>
                    <option value="TUESDAY">Tuesday</option>
                    <option value="WEDNESDAY">Wednesday</option>
                    <option value="THURSDAY">Thursday</option>
                    <option value="FRIDAY">Friday</option>
                    <option value="SATURDAY">Saturday</option>
                    <option value="SUNDAY">Sunday</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="dateOfBirth" className="form-label">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    className="form-input"
                    value={newMember.dateOfBirth}
                    onChange={(e) => {
                      const dob = e.target.value;
                      setNewMember({...newMember, dateOfBirth: dob});
                      
                      // Auto-calculate age if DOB is provided
                      if (dob) {
                        const birthDate = new Date(dob);
                        const today = new Date();
                        let age = today.getFullYear() - birthDate.getFullYear();
                        const monthDiff = today.getMonth() - birthDate.getMonth();
                        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                          age--;
                        }
                        setNewMember(prev => ({...prev, age: age.toString()}));
                      }
                    }}
                    disabled={formLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="age" className="form-label">
                    Age (years)
                  </label>
                  <input
                    type="number"
                    id="age"
                    className="form-input"
                    value={newMember.age}
                    onChange={(e) => setNewMember({...newMember, age: e.target.value})}
                    disabled={formLoading}
                    placeholder="Auto-filled from DOB"
                    min="1"
                    max="120"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="heightCm" className="form-label">
                    Height (cm)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    id="heightCm"
                    className="form-input"
                    value={newMember.heightCm}
                    onChange={(e) => {
                      const height = e.target.value;
                      setNewMember({...newMember, heightCm: height});
                      
                      // Auto-calculate ideal weight when height changes
                      if (height && newMember.gender) {
                        const idealWeight = calculateIdealWeight(height, newMember.gender);
                        if (idealWeight) {
                          setNewMember(prev => ({...prev, idealWeightKg: idealWeight.toString()}));
                        }
                      }
                    }}
                    disabled={formLoading}
                    placeholder="e.g., 175.5"
                    min="50"
                    max="250"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="initialWeightKg" className="form-label">
                    Initial Weight (kg)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    id="initialWeightKg"
                    className="form-input"
                    value={newMember.initialWeightKg}
                    onChange={(e) => setNewMember({...newMember, initialWeightKg: e.target.value})}
                    disabled={formLoading}
                    placeholder="e.g., 85.5"
                    min="20"
                    max="300"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="idealWeightKg" className="form-label">
                    Ideal Weight (kg)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    id="idealWeightKg"
                    className="form-input"
                    value={newMember.idealWeightKg}
                    onChange={(e) => setNewMember({...newMember, idealWeightKg: e.target.value})}
                    disabled={formLoading}
                    placeholder="Auto-calculated from height & gender"
                    min="20"
                    max="300"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="program" className="form-label">
                  Program *
                </label>
                <select
                  id="program"
                  className="form-input"
                  value={newMember.program}
                  onChange={(e) => setNewMember({...newMember, program: e.target.value})}
                  required
                  disabled={formLoading}
                >
                  <option value="">Select Program</option>
                  {programOptions.map(program => (
                    <option key={program} value={program}>
                      {program}
                    </option>
                  ))}
                </select>
              </div>


              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Password {editMember ? '(leave blank to keep current)' : '*'}
                </label>
                <input
                  type="password"
                  id="password"
                  className="form-input"
                  value={newMember.password}
                  onChange={(e) => setNewMember({...newMember, password: e.target.value})}
                  required={!editMember}
                  disabled={formLoading}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="programDiscountSlab" className="form-label">
                    Program Discount Slab
                  </label>
                  <select
                    id="programDiscountSlab"
                    className="form-input"
                    value={newMember.programDiscountSlabId}
                    onChange={(e) => setNewMember({...newMember, programDiscountSlabId: e.target.value})}
                    disabled={formLoading}
                  >
                    {discountPlans.map(plan => (
                      <option key={plan.id} value={plan.id}>
                        {plan.planName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="productDiscountSlab" className="form-label">
                    Product Discount Slab
                  </label>
                  <select
                    id="productDiscountSlab"
                    className="form-input"
                    value={newMember.productDiscountSlabId}
                    onChange={(e) => setNewMember({...newMember, productDiscountSlabId: e.target.value})}
                    disabled={formLoading}
                  >
                    {productDiscountPlans.map(plan => (
                      <option key={plan.id} value={plan.id}>
                        {plan.planName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="memberRole" className="form-label">
                  Member Role *
                </label>
                <select
                  id="memberRole"
                  className="form-input"
                  value={newMember.memberRoleCode}
                  onChange={(e) => setNewMember({...newMember, memberRoleCode: e.target.value})}
                  required
                  disabled={formLoading}
                >
                  <option value="CUSTOMER">Customer</option>
                  <option value="ASSOCIATE">Associate</option>
                  <option value="SUPERVISOR">Supervisor</option>
                  <option value="WORLD_TEAM">World Team</option>
                  <option value="AWT_TEAM">AWT Team</option>
                  <option value="GET_TEAM">Get Team</option>
                  <option value="MILLENNIAL_TEAM">Millennial Team</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditMember(null);
                    setFormError('');
                    setNewMember({
                      firstName: '',
                      lastName: '',
                      email: '',
                      password: '',
                      gender: 'MALE',
                      phone: '',
                      phoneCountryId: null,
                      address: '',
                      pcId: '',
                      customerType: 'ONLINE',
                      referredById: '',
                      referredBySearch: '',
                      showReferralDropdown: false,
                      filteredReferralOptions: [],
                      checkupDay: 'MONDAY',
                      program: '',
                      programStartDate: '',
                      programEndDate: '',
                      memberRoleCode: 'CUSTOMER',
                      programDiscountSlabId: '',
                      productDiscountSlabId: ''
                    });
                  }}
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={formLoading}
                >
                  {formLoading ? (editMember ? 'Updating...' : 'Adding...') : (editMember ? 'Update Member' : 'Add Customer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Member Details Modal */}
      {viewMember && memberDetails && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
                Member Details
              </h3>
              <button
                onClick={() => {
                  setViewMember(null);
                  setMemberDetails(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--gray-dark)'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>Personal Information</h4>
                <div style={{ marginBottom: '10px' }}>
                  <strong>Full Name:</strong> {memberDetails.fullName || 'N/A'}
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <strong>Email:</strong> {memberDetails.email}
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <strong>Phone:</strong> {memberDetails.phone || 'N/A'}
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <strong>Gender:</strong> {memberDetails.gender || 'N/A'}
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <strong>Address:</strong> {memberDetails.address || 'N/A'}
                </div>
              </div>

              <div>
                <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>Account Information</h4>
                <div style={{ marginBottom: '10px' }}>
                  <strong>Status:</strong> 
                  <span style={{
                    color: getStatusColor(memberDetails.status),
                    marginLeft: '5px',
                    fontWeight: '500'
                  }}>
                    {memberDetails.status}
                  </span>
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <strong>Member Role:</strong> {memberDetails.memberRole?.displayName || 'No Role'}
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <strong>PC ID/Associate ID:</strong> {memberDetails.pcId || 'N/A'}
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <strong>Customer Type:</strong> {memberDetails.customerType || 'N/A'}
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <strong>Referred By:</strong> {memberDetails.referredBy?.fullName || 'N/A'}
                </div>
              </div>
            </div>

            <div style={{ marginTop: '20px' }}>
              <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>Program Information</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <div style={{ marginBottom: '10px' }}>
                    <strong>Program:</strong> {memberDetails.program || 'N/A'}
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <strong>Checkup Day:</strong> {memberDetails.checkupDay || 'N/A'}
                  </div>
                </div>
                <div>
                  <div style={{ marginBottom: '10px' }}>
                    <strong>Program Start:</strong> {memberDetails.programStartDate ? new Date(memberDetails.programStartDate).toLocaleDateString() : 'N/A'}
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <strong>Program End:</strong> {memberDetails.programEndDate ? new Date(memberDetails.programEndDate).toLocaleDateString() : 'N/A'}
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <strong>Discount Plan:</strong> {memberDetails.discountPlan?.planName || 'None'}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: '20px' }}>
              <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>Timeline</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div style={{ marginBottom: '10px' }}>
                  <strong>Joined Network:</strong> {memberDetails.joinedAt ? new Date(memberDetails.joinedAt).toLocaleDateString() : 'N/A'}
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <strong>Account Created:</strong> {memberDetails.createdAt ? new Date(memberDetails.createdAt).toLocaleDateString() : 'N/A'}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '30px' }}>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setViewMember(null);
                  setMemberDetails(null);
                  handleEditMember(viewMember);
                }}
              >
                Edit Member
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setViewMember(null);
                  setMemberDetails(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '400px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
              {editMember ? 'Member Updated Successfully!' : 'Member Added Successfully!'}
            </h3>
            <p style={{ color: 'var(--gray-dark)', marginBottom: '25px' }}>
              {editMember ? 'The member information has been updated.' : 'The new member has been added to your network.'}
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setShowConfirmModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Members;
